import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Mail, Phone, Trash2, Save, AlertTriangle, Inbox, MailWarning } from "lucide-react";
import {
  CONTACT_REQUEST_STATUSES, PLAN_LABELS, formatTry, guestRangeLabel, trAblativeSuffix,
  type ContactEventType, type ContactRequestRow, type ContactRequestStatus, type FunnelDay,
} from "@wedding/shared";
import { adminLeadsApi } from "../lib/api";
import { cn } from "../lib/utils";

/**
 * "Satış & Site → Teklifler" — landing teklif formundan gelen talepler
 * (migration 052) + son 30 günün form hunisi.
 *
 * Migration uygulanmadıysa iki uç da `available: false` döner → bilgi kartı.
 * Her eylem toast'lı; silme kalıcıdır (KVKK silme talebi için) ve onay ister.
 */

const EVENT_KEYS: Record<ContactEventType, string> = {
  wedding: "landing.contact.evWedding",
  wedding_henna: "landing.contact.evWeddingHenna",
  henna: "landing.contact.evHenna",
  engagement: "landing.contact.evEngagement",
  promise: "landing.contact.evPromise",
  other: "landing.contact.evOther",
};

const STATUS_STYLE: Record<ContactRequestStatus, string> = {
  new: "bg-primary/10 text-primary border-primary/20",
  contacted: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  won: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20",
  lost: "bg-muted text-muted-foreground border-border",
};

const FUNNEL_ROWS = [
  { key: "view", labelKey: "siteAdmin.leads.stepView" },
  { key: "step1", labelKey: "siteAdmin.leads.stepStep1" },
  { key: "step2", labelKey: "siteAdmin.leads.stepStep2" },
  { key: "submit", labelKey: "siteAdmin.leads.stepSubmit" },
] as const;

/** D1 `datetime('now')` → "YYYY-MM-DD HH:MM:SS" (UTC). */
const parseUtc = (s: string) => new Date(`${s.replace(" ", "T")}Z`);

type Counts = Record<ContactRequestStatus, number>;

/** `bounds`: Fiyatlar'daki kademe sınırları — talepteki aralık bugünkü sayılarla gösterilir. */
export function LeadsTab({ bounds, showToast }: { bounds: readonly number[]; showToast: (msg: string, ok?: boolean) => void }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ContactRequestStatus | "all">("all");
  const [items, setItems] = useState<ContactRequestRow[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [funnel, setFunnel] = useState<FunnelDay[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await adminLeadsApi.list(filter === "all" ? {} : { status: filter });
      const data = res.data;
      if (!data || !data.available) {
        setAvailable(false);
        return;
      }
      setAvailable(true);
      setItems(data.items);
      setCounts(data.counts);
      setNextCursor(data.nextCursor);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    adminLeadsApi
      .funnel(30)
      .then((res) => setFunnel(res.data?.available ? res.data.days : null))
      .catch(() => setFunnel(null));
  }, []);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await adminLeadsApi.list({ ...(filter === "all" ? {} : { status: filter }), cursor: nextCursor });
      const data = res.data;
      if (data?.available) {
        setItems((prev) => [...prev, ...data.items.filter((r) => !prev.some((p) => p.id === r.id))]);
        setNextCursor(data.nextCursor);
      }
    } catch (err) {
      showToast(t("siteAdmin.leads.failed", { error: err instanceof Error ? err.message : String(err) }), false);
    } finally {
      setLoadingMore(false);
    }
  };

  const onUpdated = (id: string, patch: Partial<ContactRequestRow>) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    if (patch.status) {
      const old = items.find((r) => r.id === id)?.status;
      if (old && counts && old !== patch.status) {
        setCounts({ ...counts, [old]: counts[old] - 1, [patch.status]: counts[patch.status] + 1 });
      }
    }
  };

  const onDeleted = (row: ContactRequestRow) => {
    setItems((prev) => prev.filter((r) => r.id !== row.id));
    if (counts) setCounts({ ...counts, [row.status]: Math.max(0, counts[row.status] - 1) });
  };

  if (!available) {
    return (
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 px-5 py-5 flex gap-4">
        <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-foreground">{t("siteAdmin.leads.unavailableTitle")}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("siteAdmin.leads.unavailableBody")}</p>
        </div>
      </div>
    );
  }

  const total = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : null;

  return (
    <div className="space-y-6">
      {funnel && <FunnelCard days={funnel} />}

      <div className="flex flex-wrap gap-2" role="group" aria-label={t("siteAdmin.leads.statusLabel")}>
        {(["all", ...CONTACT_REQUEST_STATUSES] as const).map((s) => {
          const n = s === "all" ? total : counts?.[s] ?? null;
          return (
            <button
              key={s}
              type="button"
              aria-pressed={filter === s}
              onClick={() => setFilter(s)}
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors cursor-pointer",
                filter === s
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40",
              )}
            >
              {s === "all" ? t("siteAdmin.leads.filterAll") : t(`siteAdmin.leads.status.${s}`)}
              {n !== null && <span className="tabular-nums text-xs opacity-80">{n}</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : loadError ? (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 px-5 py-5 flex items-center gap-4">
          <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
          <p className="text-sm flex-1">{t("siteAdmin.leads.loadFailed")}</p>
          <button type="button" onClick={() => void load()}
            className="rounded-full border border-border px-4 py-2 text-sm hover:bg-primary/5 cursor-pointer">
            {t("siteAdmin.retry")}
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-14 flex flex-col items-center gap-3 text-muted-foreground">
          <Inbox className="h-7 w-7" />
          <p className="text-sm">{t("siteAdmin.leads.empty")}</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((row) => (
            <LeadCard key={row.id} row={row} bounds={bounds} onUpdated={onUpdated} onDeleted={onDeleted} showToast={showToast} />
          ))}
        </ul>
      )}

      {nextCursor && !loading && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm hover:bg-primary/5 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          >
            {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t("siteAdmin.leads.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
}

function FunnelCard({ days }: { days: FunnelDay[] }) {
  const { t } = useTranslation();
  const totals = days.reduce(
    (acc, d) => ({ view: acc.view + d.view, step1: acc.step1 + d.step1, step2: acc.step2 + d.step2, submit: acc.submit + d.submit }),
    { view: 0, step1: 0, step2: 0, submit: 0 },
  );
  const max = Math.max(1, totals.view, totals.step1, totals.step2, totals.submit);
  return (
    <div className="rounded-2xl border border-border bg-card px-5 py-4">
      <p className="text-sm font-semibold text-foreground">{t("siteAdmin.leads.funnelTitle")}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.leads.funnelHint")}</p>
      <dl className="mt-4 space-y-2.5">
        {FUNNEL_ROWS.map(({ key, labelKey }) => {
          const n = totals[key];
          const pct = key === "view" || totals.view === 0 ? null : Math.round((n / totals.view) * 100);
          return (
            <div key={key} data-testid={`funnel-${key}`} className="grid grid-cols-[9rem_1fr_auto] sm:grid-cols-[12rem_1fr_auto] items-center gap-3">
              <dt className="text-xs text-muted-foreground truncate">{t(labelKey)}</dt>
              <div className="h-2 rounded-full bg-muted overflow-hidden" aria-hidden="true">
                <div className="h-full rounded-full bg-primary" style={{ width: `${(n / max) * 100}%` }} />
              </div>
              <dd className="text-sm font-semibold tabular-nums text-foreground min-w-16 text-right">
                {n}
                {pct !== null && (
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">{t("siteAdmin.leads.pct", { pct })}</span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function LeadCard({ row, bounds, onUpdated, onDeleted, showToast }: {
  row: ContactRequestRow;
  bounds: readonly number[];
  onUpdated: (id: string, patch: Partial<ContactRequestRow>) => void;
  onDeleted: (row: ContactRequestRow) => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [note, setNote] = useState(row.admin_note);
  const [busy, setBusy] = useState<"status" | "note" | "delete" | null>(null);
  const fail = (err: unknown) =>
    showToast(t("siteAdmin.leads.failed", { error: err instanceof Error ? err.message : String(err) }), false);

  const created = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(parseUtc(row.created_at));
  const eventDate = new Intl.DateTimeFormat(i18n.language, { dateStyle: "long", timeZone: "UTC" }).format(parseUtc(`${row.event_date} 00:00:00`));
  const estimate =
    row.estimate_try === null
      ? null
      : row.estimate_from
        ? `${formatTry(row.estimate_try, "tr")}’${trAblativeSuffix(row.estimate_try)}`
        : formatTry(row.estimate_try, "tr");

  const changeStatus = async (status: ContactRequestStatus) => {
    setBusy("status");
    try {
      await adminLeadsApi.patch(row.id, { status });
      onUpdated(row.id, { status });
      showToast(t("siteAdmin.leads.updated"));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const saveNote = async () => {
    setBusy("note");
    try {
      await adminLeadsApi.patch(row.id, { adminNote: note });
      onUpdated(row.id, { admin_note: note.trim() });
      showToast(t("siteAdmin.leads.updated"));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("siteAdmin.leads.deleteConfirm", { name: row.name }))) return;
    setBusy("delete");
    try {
      await adminLeadsApi.remove(row.id);
      onDeleted(row);
      showToast(t("siteAdmin.leads.deleted"));
    } catch (err) {
      fail(err);
      setBusy(null);
    }
  };

  return (
    <li data-testid={`lead-${row.id}`} className="rounded-2xl border border-border bg-card px-5 py-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground flex flex-wrap items-center gap-2">
            {row.name}
            <span className={cn("text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border", STATUS_STYLE[row.status])}>
              {t(`siteAdmin.leads.status.${row.status}`)}
            </span>
            {row.email_sent === 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20">
                <MailWarning className="h-3 w-3" />
                {t("siteAdmin.leads.emailNotSent")}
              </span>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t(EVENT_KEYS[row.event_type])} · {eventDate} ·{" "}
            {/* Yeni form misafir sayısını PAKETLE sorar (sayılar paket tanımından); eski talepte aralık. */}
            {row.plan === "custom"
              ? t("siteAdmin.leads.planCustom")
              : row.plan
                ? t("siteAdmin.leads.planChosen", { plan: PLAN_LABELS[row.plan] })
                : t("landing.plans.tierGuests", { range: guestRangeLabel(t, bounds, row.guest_range) })}
            {row.city ? ` · ${row.city}` : ""}
          </p>
        </div>
        <div className="sm:text-right shrink-0">
          <p className="text-sm font-semibold tabular-nums text-foreground">{estimate ?? t("siteAdmin.leads.noEstimate")}</p>
          <p className="text-[11px] text-muted-foreground">{created}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-sm">
        <a href={`mailto:${row.email}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 hover:bg-primary/5 cursor-pointer">
          <Mail className="h-3.5 w-3.5" /> {row.email}
        </a>
        <a href={`tel:${row.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 hover:bg-primary/5 cursor-pointer tabular-nums">
          <Phone className="h-3.5 w-3.5" /> {row.phone}
        </a>
      </div>

      {row.message && (
        <p className="text-sm text-foreground/90 whitespace-pre-wrap rounded-xl bg-muted/40 px-3 py-2">{row.message}</p>
      )}

      <div className="grid gap-3 sm:grid-cols-[12rem_1fr_auto] sm:items-start">
        <select
          value={row.status}
          disabled={busy !== null}
          onChange={(e) => void changeStatus(e.target.value as ContactRequestStatus)}
          aria-label={t("siteAdmin.leads.statusLabel")}
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
        >
          {CONTACT_REQUEST_STATUSES.map((s) => (
            <option key={s} value={s}>{t(`siteAdmin.leads.status.${s}`)}</option>
          ))}
        </select>
        <div className="flex gap-2">
          <input
            value={note}
            maxLength={1000}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("siteAdmin.leads.notePh")}
            aria-label={t("siteAdmin.leads.note")}
            className="min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <button
            type="button"
            onClick={() => void saveNote()}
            disabled={busy !== null || note.trim() === row.admin_note}
            aria-label={t("siteAdmin.leads.saveNote")}
            title={t("siteAdmin.leads.saveNote")}
            className="shrink-0 rounded-xl border border-border px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-primary/5 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
          >
            {busy === "note" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          </button>
        </div>
        <button
          type="button"
          onClick={() => void remove()}
          disabled={busy !== null}
          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-red-500/30 px-3 py-2 text-sm text-red-500 hover:bg-red-500/5 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
        >
          {busy === "delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          {t("siteAdmin.leads.delete")}
        </button>
      </div>
    </li>
  );
}
